import React, { useEffect, useState } from 'react'
import * as yup from "yup";
import { Label } from '../labels'
import { ErrorState } from '../Types';
import { RadioList, Select } from '../controls';
import { TextInput } from '../inputs';
import { Spinner } from '../layout';
import { AbnPicker, Address, AddressPicker, AddressSchema } from '../pickers';
import { ValidationHint } from '../validation';

export interface ClientPreview {
    name?: string;
    abn?: string;
    email?: string;
}

export interface ClientReference {
    clientId?: string;
    name?: string;
}

export enum ClientTypes {
    Business = "Business",
    Individual = "Individual",
}

export const getClientShape = (getClientById: (clientId: string) => Promise<ClientPreview | null | undefined>) => ({
    clientIdKnown: yup.boolean().required().label("Customer ID"),
    clientType: yup
        .mixed<ClientTypes>()
        .oneOf(Object.values(ClientTypes))
        .when("clientIdKnown", {
            is: (clientIdKnown?: boolean) => clientIdKnown === false,
            then: (schema) => schema.required(),
        })
        .label("Question"),
    clientId: yup
        .string()
        .when("clientIdKnown", {
            is: (clientIdKnown?: boolean) => clientIdKnown === true,
            then: (schema) =>
                schema.required().test(async (v) => {
                    if (!v) {
                        return true;
                    }
                    try {
                        const res = await getClientById(v);
                        return res ? true : false;
                    }
                    catch {
                        return false;
                    }
                }),
        })
        .when(["clientIdKnown", "clientType", "clientABN", "clientEmail"], {
            is: (
                clientIdKnown?: boolean,
                clientType?: ClientTypes,
                clientABN?: string,
                clientEmail?: string
            ) => {
                if (clientIdKnown !== false) return false;
                return (clientType == ClientTypes.Business && clientABN) ||
                    (clientType == ClientTypes.Individual && clientEmail)
                    ? true
                    : false;
            },
            then: (schema) => schema.required(),
        })
        .label("Customer ID"),
    clientABN: yup
        .string()
        .when(["clientIdKnown", "clientType"], {
            is: (clientIdKnown?: boolean, clientType?: ClientTypes) =>
                clientIdKnown === false && clientType === ClientTypes.Business,
            then: (schema) => schema.required(),
        })
        .label("ABN"),
    clientEmail: yup
        .string()
        .when(["clientIdKnown", "clientType", "clientId"], {
            is: (
                clientIdKnown?: boolean,
                clientType?: ClientTypes,
                clientId?: string
            ) =>
                (clientIdKnown === false &&
                    clientType === ClientTypes.Individual) ||
                clientId == "new",
            then: (schema) => schema.required(),
        })
        .label("Email"),
    clientName: yup
        .string()
        .label("Trading Name")
        .when("clientId", {
            is: (clientId?: string) => clientId == "new",
            then: (schema) => schema.required(),
        }),
    clientFirstName: yup
        .string()
        .label("First Name")
        .when("clientId", {
            is: (clientId?: string) => clientId == "new",
            then: (schema) => schema.required(),
        }),
    clientLastName: yup
        .string()
        .label("Last Name")
        .when("clientId", {
            is: (clientId?: string) => clientId == "new",
            then: (schema) => schema.required(),
        }),
    clientBusinessPhone: yup
        .string()
        .label("Main Phone")
        .when("clientId", {
            is: (clientId?: string) => clientId == "new",
            then: (schema) => schema.required(),
        }),
    clientMobile: yup.string().label("Mobile"),
    clientOtherPhone: yup.string().label("Other Phone"),
    clientPostalAddress: AddressSchema.when("clientId", {
        is: (clientId?: string) => clientId == "new",
        then: (schema) => schema.required("Address is required"),
    }).default(undefined),
    clientBusinessAddress: AddressSchema.default(undefined)
})

export const clientShapeExcludes:[string, string][] = [["clientEmail", "clientId"]];

export interface Client {
    clientType?: ClientTypes;
    clientId?: string;
    clientIdKnown?: boolean;
    clientName?: string;
    clientABN?: string;
    clientFirstName?: string;
    clientLastName?: string;
    clientBusinessPhone?: string;
    clientMobile?: string;
    clientEmail?: string;
    clientOtherPhone?: string;
    clientPostalAddress?: Address;
    clientBusinessAddress?: Address;
}

export interface ClientPickerProps {
    value: Client
    onChange: (v: Partial<Client>, hint?: ValidationHint) => any
    getClientById: (clientId: string) => Promise<ClientPreview | null | undefined>
    getClientsByAbn: (abn: string) => Promise<ClientReference[]>
    getClientsByEmail: (email: string) => Promise<ClientReference[]>
    errors?: ErrorState<Client>
    readOnly?: boolean
    className?: string
}

export const ClientPicker: React.FC<ClientPickerProps> = ({ value, onChange, errors, readOnly, className, ...props }) => {
    const [registeredName, setRegisteredName] = useState<string>("");
    const [clientDetails, setClientDetails] = useState<ClientPreview | null>(null);
    const [clientReferences, setClientReferences] = useState<ClientReference[] | null>(null);

    const setClientId = async (clientId?: string) => {
        onChange({ clientId });
        if (clientId && clientId != "new") {
            try {
                const d = await props.getClientById(clientId);
                setClientDetails(d ? d : {});
                if(d) {
                    onChange({
                        clientId: clientId,
                        clientType: d.abn
                            ? ClientTypes.Business
                            : ClientTypes.Individual,
                        clientABN: d.abn,
                        clientName: d.name,
                        clientEmail: d.email
                    }, ValidationHint.NoValidation);    
                }
                else {
                    onChange({
                        clientId: clientId,
                        clientType: undefined,
                        clientABN: undefined,
                        clientName: undefined,
                        clientEmail: undefined
                    }, ValidationHint.NoValidation);
                }
            }
            catch {
                setClientDetails({});
                onChange({
                    clientId: clientId,
                    clientType: undefined,
                    clientABN: undefined,
                    clientName: undefined,
                    clientEmail: undefined
                }, ValidationHint.NoValidation);
            }
        }
    }

    useEffect(() => {
        if (value.clientId && value.clientId != "new") {
            props.getClientById(value.clientId).then(d => {
                setClientDetails(d ? d : {});
            }).catch(() => {
                setClientDetails({});
            });
        }
    }, []);

    useEffect(() => {
        onChange({
            clientEmail: value.clientType == ClientTypes.Business ? undefined : value.clientEmail,
            clientABN: value.clientType == ClientTypes.Individual ? undefined : value.clientABN,
        }, ValidationHint.NoValidation);
    }, [value.clientType]);

    useEffect(() => {
        setClientReferences(null);
        var res: Promise<ClientReference[]>;
        if (value.clientABN) {
            res = props.getClientsByAbn(value.clientABN)
        }
        else if (value.clientEmail) {
            res = props.getClientsByEmail(value.clientEmail)
        }
        else {
            res = Promise.resolve([]);
        }

        res.then(d => {
            setClientReferences(d)
            if (value.clientId != "new" && d.filter(r => r.clientId == value.clientId).length == 0) {
                onChange({ clientId: undefined }, ValidationHint.NoValidation);
            }
        }).catch(() => {
            onChange({ clientId: undefined }, ValidationHint.NoValidation);
            setClientReferences([])
        });

    }, [value.clientABN, value.clientABN ? null : value.clientEmail]);

    return <div className={className}>
        <div className="row">
            <div className="col col-12">
                <Label
                    id="clientIdKnown"
                    label="Do you have a customer ID?"
                    error={errors?.clientIdKnown}
                    required
                >
                    <RadioList<boolean | undefined>
                        disabled={readOnly}
                        id="clientIdKnown"
                        value={value.clientIdKnown}
                        options={[
                            {
                                label: "I have a customer ID",
                                value: true,
                            },
                            {
                                label: "I don't have a customer ID",
                                value: false,
                            },
                        ]}
                        onChange={(clientIdKnown) => onChange({ clientIdKnown })}
                    />
                </Label>
            </div>
        </div>

        {value.clientIdKnown === true && (
            <>
                <div className="row mt-3">
                    <div className="col col-6">
                        <Label
                            id="clientId"
                            label="Customer ID"
                            error={errors?.clientId}
                            required={true}
                        >
                            <TextInput
                                disabled={readOnly}
                                value={value.clientId || ""}
                                onChange={(clientId) => setClientId(clientId)}
                                maxLength={100}
                            />
                        </Label>
                    </div>
                </div>

                {value.clientId && (
                    <>
                        <Spinner
                            isLoading={clientDetails == null ? true : false}
                            loadingText=" "
                        >
                            <div className="row mt-3">
                                {clientDetails?.name && (
                                    <>
                                        <div className="col col-6">
                                            <Label id="clientPreview.name" label="Name">
                                                <TextInput
                                                    readonly={true}
                                                    value={clientDetails?.name || ""}
                                                    onChange={() => { }}
                                                />
                                            </Label>
                                        </div>
                                    </>
                                )}
                                {clientDetails?.abn && (
                                    <>
                                        <div className="col col-6">
                                            <Label id="clientPreview.abn" label="ABN">
                                                <TextInput
                                                    readonly={true}
                                                    value={clientDetails?.abn || ""}
                                                    onChange={() => { }}
                                                />
                                            </Label>
                                        </div>
                                    </>
                                )}
                                {clientDetails?.email && (
                                    <>
                                        <div className="col col-6">
                                            <Label id="clientPreview.email" label="Email">
                                                <TextInput
                                                    readonly={true}
                                                    value={clientDetails?.email || ""}
                                                    onChange={() => { }}
                                                />
                                            </Label>
                                        </div>
                                    </>
                                )}
                            </div>
                        </Spinner>
                    </>
                )}
            </>
        )}

        {value.clientIdKnown === false && (
            <>
                <div className="row mt-4">
                    <div className="col col-12">
                        <Label
                            id="clientType"
                            label="Are you applying as"
                            error={errors?.clientType}
                            required
                        >
                            <RadioList<ClientTypes | undefined>
                                disabled={readOnly}
                                id="clientType"
                                value={value.clientType}
                                options={[
                                    {
                                        label: "Body corporate",
                                        value: ClientTypes.Business,
                                    },
                                    {
                                        label: "Individual",
                                        value: ClientTypes.Individual,
                                    },
                                ]}
                                onChange={(clientType) => onChange({ clientType })}
                            />
                        </Label>
                    </div>
                </div>

                {value.clientType == ClientTypes.Business && (
                    <>
                        <div className="row mt-3">
                            <div className="col col-6">
                                <Label
                                    id="clientAbn"
                                    label="ABN"
                                    error={errors?.clientABN}
                                    required
                                >
                                    <AbnPicker
                                        value={value.clientABN || null}
                                        onChange={(v) => {
                                            var clientABN = v?.abn || undefined;
                                            if (value.clientABN != clientABN) {
                                                onChange({ clientABN });
                                            }
                                            setRegisteredName(v?.entityName || "");
                                        }}
                                    />
                                </Label>
                            </div>
                            <div className="col col-6">
                                <Label id="regiseteredName" label="Registered Name">
                                    <TextInput
                                        readonly={true}
                                        value={registeredName || ""}
                                        onChange={() => { }}
                                    />
                                </Label>
                            </div>
                        </div>
                    </>
                )}

                {value.clientType == ClientTypes.Individual && (
                    <>
                        <div className="row mt-3">
                            <div className="col col-6">
                                <Label
                                    id="clientEmail"
                                    label="Email linked to Customer ID"
                                    error={errors?.clientEmail}
                                    required
                                >
                                    <TextInput
                                        value={value.clientEmail || ""}
                                        onChange={(clientEmail) =>
                                            onChange({ clientEmail })
                                        }
                                        maxLength={400}
                                    />
                                </Label>
                            </div>
                        </div>
                    </>
                )}

                {(value.clientEmail || value.clientABN) && (
                    <>
                        <div className="row mt-3">
                            <div className="col col-6">
                                <Spinner
                                    isLoading={clientReferences == null}
                                    loadingText=" "
                                >
                                    <Label
                                        id="clientId2"
                                        label="Customer ID"
                                        error={errors?.clientId}
                                        required
                                    >
                                        <Select<string>
                                            options={
                                                clientReferences?.map(r => ({
                                                    label: `${r.clientId} - ${r.name}`,
                                                    value: r.clientId!,
                                                })).concat({ label: "New...", value: "new" }) || []
                                            }
                                            value={value.clientId || null}
                                            onChange={(clientId) =>
                                                setClientId(clientId || undefined)
                                            }
                                            defaultLabel="Select..."
                                            size="block"
                                        />
                                    </Label>
                                </Spinner>
                            </div>
                        </div>
                    </>
                )}

                {value.clientId == "new" && (
                    <>
                        <div className="row mt-3">
                            <div className="col col-12">
                                <Label
                                    id="clientName"
                                    label="Trading Name"
                                    error={errors?.clientName}
                                    required
                                >
                                    <TextInput
                                        value={value.clientName || ""}
                                        onChange={(clientName) => onChange({ clientName })}
                                        maxLength={400}
                                    />
                                </Label>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col col-6">
                                <Label
                                    id="clientFirstname"
                                    label="First Name"
                                    error={errors?.clientFirstName}
                                    required
                                >
                                    <TextInput
                                        value={value.clientFirstName || ""}
                                        onChange={(clientFirstName) =>
                                            onChange({ clientFirstName })
                                        }
                                        maxLength={200}
                                    />
                                </Label>
                            </div>

                            <div className="col col-6">
                                <Label
                                    id="clientLastName"
                                    label="Last Name"
                                    error={errors?.clientLastName}
                                    required
                                >
                                    <TextInput
                                        value={value.clientLastName || ""}
                                        onChange={(clientLastName) =>
                                            onChange({ clientLastName })
                                        }
                                        maxLength={200}
                                    />
                                </Label>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col col-6">
                                <Label
                                    id="clientBusinessPhone"
                                    label="Main phone"
                                    error={errors?.clientBusinessPhone}
                                    required
                                >
                                    <TextInput
                                        value={value.clientBusinessPhone || ""}
                                        onChange={(clientBusinessPhone) =>
                                            onChange({ clientBusinessPhone })
                                        }
                                        maxLength={100}
                                    />
                                </Label>
                            </div>

                            <div className="col col-6">
                                <Label
                                    id="clientMobile"
                                    label="Mobile"
                                    error={errors?.clientMobile}
                                >
                                    <TextInput
                                        value={value.clientMobile || ""}
                                        onChange={(clientMobile) =>
                                            onChange({ clientMobile })
                                        }
                                        maxLength={100}
                                    />
                                </Label>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col col-6">
                                {value.clientType == ClientTypes.Business && (
                                    <>
                                        <Label
                                            id="clientEmail2"
                                            label="Email"
                                            error={errors?.clientEmail}
                                            required
                                        >
                                            <TextInput
                                                value={value.clientEmail || ""}
                                                onChange={(clientEmail) =>
                                                    onChange({ clientEmail })
                                                }
                                                maxLength={400}
                                            />
                                        </Label>
                                    </>
                                )}
                            </div>

                            <div className="col col-6">
                                <Label
                                    id="clientOtherPhone"
                                    label="Other phone"
                                    error={errors?.clientOtherPhone}
                                >
                                    <TextInput
                                        value={value.clientOtherPhone || ""}
                                        onChange={(clientOtherPhone) =>
                                            onChange({ clientOtherPhone })
                                        }
                                        maxLength={100}
                                    />
                                </Label>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col col-6">
                                <Label
                                    id="clientPostalAddress"
                                    label="Postal Address"
                                    error={errors?.clientPostalAddress}
                                    required
                                >
                                    <AddressPicker
                                        value={value.clientPostalAddress || null}
                                        onChange={(clientPostalAddress) =>
                                            onChange({
                                                clientPostalAddress:
                                                    clientPostalAddress || undefined,
                                            })
                                        }
                                    />
                                </Label>
                            </div>

                            <div className="col col-6">
                                <Label
                                    id="clientPhysicalAddress"
                                    label="Business Address"
                                    error={errors?.clientBusinessAddress}
                                >
                                    <AddressPicker
                                        value={value.clientBusinessAddress || null}
                                        onChange={(clientBusinessAddress) =>
                                            onChange({
                                                clientBusinessAddress:
                                                    clientBusinessAddress || undefined,
                                            })
                                        }
                                    />
                                </Label>
                            </div>
                        </div>
                    </>
                )}
            </>
        )}
    </div>
}